import Moment from 'moment';
import {PERIOD_BUDGET_TYPE, PERIODICITY} from '../../../../constants';
import Util from '../../../shared/util/util';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {CreateMomentDate} from '../../../shared/util/DateUtil';
import {convertPriceToInvoiceCurrency} from '../../../../components/insights/invoicing/line-generation/invoicing_generate_lines_utils';
import RoundingUtility, {RoundingEntities} from '../../../shared/util/RoundingUtil';
import {getTotalPrice} from '../../../../components/insights/invoicing/line-generation/invoicing_generate_lines_fixedhours_retainer';
import {RateCardUtil} from '../../../shared/util/RateCardUtil';
import {hasPermission} from '../../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../Permissions';

/**
 * A time registration conflicts with its locked period when it was created after the lock and its
 * conflict has not been handled. Creation time is compared rather than the last update, because
 * system writes after a lock (approving the period's time, for example) also move `updatedAt`, and
 * entries created before the lock are read-only, so a later update is never a user edit.
 */
export const timeRegIsInConflict = (timeReg, period) => {
	const createdDate = Moment(timeReg.createdAt);
	const lockedDate = Moment(period.periodLockedTime);
	return period.periodLocked && !timeReg.retainerConflictHandled && createdDate.isAfter(lockedDate);
};

/**
 * Whether the viewer may handle the conflicts of a period. Accepting T&M conflicts adds time to a
 * locked period, so it needs what locking and unlocking need: retainer period update permission,
 * and either the hours view or financial access. Fixed Hours conflict handling is not gated here.
 */
export const canFixPeriodConflicts = (period, inHours, hasFinancialAccess) => {
	if (period.periodBudgetType !== PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS) return true;
	return hasPermission(PERMISSION_TYPE.RETAINER_PERIOD_UPDATE) && (!!inHours || !!hasFinancialAccess);
};

/**
 * T2-62: Total price of the part-of-fixed-price expenses associated with a Fixed Price retainer
 * period. Such expenses consume the period's fixed-price budget, so this total is subtracted when
 * computing the rollover amount on period lock (mirrors Actual BVS = time x rate + sum(price) of
 * part-of-fixed-price period expenses). Returns 0 when the feature flag is off or there are no
 * expenses on the period.
 *
 * @param {{expenses?: {edges: Array<{node: {price: ?number}}>}}} period retainer period node
 * @returns {number} summed expense price for the period
 */
export function getPeriodExpensesTotal(period) {
	if (!hasFeatureFlag('expenses_on_fixed_price_retainer_periods')) {
		return 0;
	}
	const expenseEdges = period && period.expenses ? period.expenses.edges : [];
	return expenseEdges.reduce((total, edge) => total + (edge.node.price || 0), 0);
}

export function getLastPeriodEndDateForSelectedPeriodCount(
	latestPeriodEndDate,
	numNewPeriods,
	defaultPeriodLength,
	defaultPeriodPeriodicity
) {
	if (!latestPeriodEndDate || numNewPeriods < 1 || !defaultPeriodLength) {
		return null;
	}
	if (defaultPeriodPeriodicity === PERIODICITY.MONTHLY) {
		const lastPeriodIsEndOfMonth = latestPeriodEndDate.isSame(latestPeriodEndDate.clone().endOf('month'), 'day');
		if (lastPeriodIsEndOfMonth) {
			return latestPeriodEndDate.clone().startOf('month').add(numNewPeriods, 'months').endOf('month');
		} else {
			return latestPeriodEndDate.clone().add(numNewPeriods, 'months');
		}
	} else {
		return latestPeriodEndDate
			.clone()
			.add(defaultPeriodLength * numNewPeriods, defaultPeriodPeriodicity === PERIODICITY.DAILY ? 'days' : 'weeks');
	}
}

export function getTimeRegsForPeriod(timeRegs, period, removeConflicts) {
	const sortByTimeRegDate = (a, b) => {
		const aDate = Util.CreateNonUtcMomentDate(a.node.year, a.node.month, a.node.day);
		const bDate = Util.CreateNonUtcMomentDate(b.node.year, b.node.month, b.node.day);
		if (aDate.isAfter(bDate)) return 1;
		if (bDate.isAfter(aDate)) return -1;
		return 0;
	};

	const startDate = Util.CreateMomentDate(period.startYear, period.startMonth, period.startDay);
	const endDate = Util.CreateMomentDate(period.endYear, period.endMonth, period.endDay);
	const timeRegWithinDate = timeReg => {
		const tDate = Util.CreateMomentDate(timeReg.node.year, timeReg.node.month, timeReg.node.day);
		return tDate && tDate.isSameOrAfter(startDate) && tDate.isSameOrBefore(endDate);
	};
	if (removeConflicts) {
		return timeRegs
			.filter(timeReg => timeRegWithinDate(timeReg) && !timeRegIsInConflict(timeReg.node, period))
			.sort(sortByTimeRegDate);
	}
	return timeRegs.filter(timeReg => timeRegWithinDate(timeReg)).sort(sortByTimeRegDate);
}

function getCSVData(timeRegs, currency, intl) {
	const getReportedTime = entry => {
		const {task, project} = entry.node;
		return {
			tid: task && task.companyTaskId ? task.companyTaskId : '',
			name: task && task.name ? task.name : project.name,
			date: Util.CreateMomentDate(entry.node.year, entry.node.month, entry.node.day).format('D MMM YYYY'),
			timeEntry: Util.convertMinutesToFullHour(entry.node.billableMinutesRegistered, intl),
			price: Util.GetCurrencySymbol(currency) + entry.node.price,
			person: entry.node.person.firstName + ' ' + entry.node.person.lastName,
			period: entry.node.period.name,
		};
	};

	let csvData = [];
	csvData.push({
		tid: 'T-ID',
		taskProject: 'Task/Project',
		date: 'Date',
		timeEntry: 'Time Entry',
		price: 'Price',
		person: 'Person',
		period: 'Period',
	});
	timeRegs.forEach(entry => {
		csvData.push(getReportedTime(entry));
	});
	return csvData;
}

export function handleCsvDownloadPress(timeRegs, currency, intl) {
	const csvData = getCSVData(timeRegs, currency, intl);
	const exportData = Util.JSONToCSV(csvData, currency, intl);
	const fileName = `reported_time.csv`.toLowerCase();
	Util.exportToCSV(exportData, fileName);
}

export function addPeriodIdToTimeReg(timeReg, period) {
	return {node: {...timeReg.node, period}};
}

/**
 * T2-99: average actual billable hours and profit across retainer periods that have already
 * ended (excludes the current/still-open period, which has no final actuals yet).
 *
 * @param {{edges: Array<{node: {endYear: number, endMonth: number, endDay: number, financialNumbers: {
 *   billableActualMinutes: number, retainerPeriodTargetPrice: number, retainerPeriodRolloverPrice: number, actualCost: number
 * }}}>}} retainerPeriods
 * @returns {{actualMinutes: ?number, profit: ?number}} null for both fields when no period has ended yet
 */
export function calculatePastPeriodsAverages(retainerPeriods) {
	const today = Moment();
	const pastPeriods = retainerPeriods.edges
		.map(edge => edge.node)
		.filter(node => {
			const periodEndDate = Util.CreateMomentDate(node.endYear, node.endMonth, node.endDay);
			return periodEndDate.isBefore(today, 'day');
		});

	if (!pastPeriods.length) {
		return {actualMinutes: null, profit: null};
	}

	const sums = pastPeriods.reduce(
		(acc, node) => ({
			actualMinutes: acc.actualMinutes + node.financialNumbers.billableActualMinutes,
			price:
				acc.price + node.financialNumbers.retainerPeriodTargetPrice + node.financialNumbers.retainerPeriodRolloverPrice,
			cost: acc.cost + node.financialNumbers.actualCost,
		}),
		{actualMinutes: 0, price: 0, cost: 0}
	);

	const count = pastPeriods.length;
	return {
		actualMinutes: sums.actualMinutes / count,
		profit: sums.price / count - sums.cost / count,
	};
}

export const sortByStartDate = (a, b) => {
	const aNode = a.node || a;
	const bNode = b.node || b;
	const aDate = Util.CreateNonUtcMomentDate(aNode.startYear, aNode.startMonth, aNode.startDay);
	const bDate = Util.CreateNonUtcMomentDate(bNode.startYear, bNode.startMonth, bNode.startDay);
	if (!aDate || !bDate) return 0;
	if (aDate.isBefore(bDate)) return -1;
	if (bDate.isBefore(aDate)) return 1;
	return 0;
};

export const getRolloversFromPeriods = (retainerPeriods, periodIds) => {
	const rollovers = [];
	retainerPeriods.edges
		.map(retainerPeriod => retainerPeriod.node)
		.forEach(retainerPeriod => {
			const periodRolloversFromPeriod = retainerPeriod.retainerPeriodRollovers.edges
				.filter(rollover => rollover.node.periodFrom && periodIds.includes(rollover.node.periodFrom.id))
				.map(rollover => rollover.node);
			periodRolloversFromPeriod.forEach(rollover => rollovers.push(rollover));
		});
	return rollovers;
};

export const getRevertableRolloversFromPeriods = (retainerPeriods, periodIds) => {
	const rollovers = getRolloversFromPeriods(retainerPeriods, periodIds);
	// A rollover is revertable if the impacted period is not locked
	return rollovers.filter(rollover => rollover.periodTo.periodLocked === false);
};

export function getUnitPrice({period, invoiceCurrency, rateCard, company, timeRegs}) {
	const hasRateRounding = hasFeatureFlag('rate_rounding');
	if (period?.periodBudgetType === 'FIXED_HOURS') {
		const projectCurrency = rateCard?.currency || company?.currency || '';
		const quantity = period.periodHoursAmount;
		const periodStartDate = CreateMomentDate(period.startYear, period.startMonth, period.startDay);
		const periodEndDate = CreateMomentDate(period.endYear, period.endMonth, period.endDay);
		const uninvoicedTimeRegs = timeRegs.filter(timeReg => !timeReg.node.invoiced);
		const unInvoicedPeriodTimeRegs = getTimeRegsForPeriod(uninvoicedTimeRegs, period);
		const totalTimeRegAmount = unInvoicedPeriodTimeRegs.reduce(
			(acc, timeReg) => acc + timeReg.node.billableMinutesRegistered,
			0
		);
		const periodTargetMinutes = period.periodHoursAmount * 60;
		const rates = unInvoicedPeriodTimeRegs.map(timeReg => timeReg.node.rate);
		const allSameRate = rates.every(rate => rate === rates[0]);

		let defaultRate = 0;
		if (rateCard) {
			const rateCardUtil = new RateCardUtil(rateCard);
			defaultRate = rateCardUtil.getAverageRateBetweenDates(null, periodStartDate, periodEndDate);
		}

		if (allSameRate && (totalTimeRegAmount >= periodTargetMinutes || defaultRate === rates[0]) && rates.length > 0) {
			return hasRateRounding
				? RoundingUtility.roundBasedOnEntityType(rates[0], RoundingEntities.RATE.name)
				: Math.round(rates[0] * 100) / 100;
		}

		const totalPrice = convertPriceToInvoiceCurrency(
			getTotalPrice(
				unInvoicedPeriodTimeRegs.map(timeReg => timeReg.node) || [],
				periodStartDate,
				periodEndDate,
				quantity,
				defaultRate
			),
			projectCurrency,
			company,
			{currency: invoiceCurrency}
		);
		return !!quantity && totalPrice
			? hasRateRounding
				? RoundingUtility.roundBasedOnEntityType(totalPrice / quantity, RoundingEntities.RATE.name)
				: Math.round((totalPrice / quantity) * 100) / 100
			: 0;
	} else if (period?.periodBudgetType === 'FIXED_PRICE') {
		return period?.periodPriceAmount || 0;
	}
	return 0;
}

export function getEyeOptions(hasTarget, hasAnyRollover, inHours) {
	const options = [
		{
			name: 'dates',
			checked: true,
			translationId: 'common.dates',
		},
		{
			name: 'target',
			checked: true,
			translationId: inHours ? 'retainer.period_target' : 'retainer.period_price',
			condition: hasTarget,
		},
		{
			name: 'net-rollover',
			checked: true,
			translationId: 'retainer.net_rollover',
			condition: hasTarget && hasAnyRollover,
		},
		{
			name: 'adjusted-target',
			checked: true,
			translationId: inHours ? 'retainer.adjusted_target' : 'retainer.adjusted_price',
			condition: hasTarget && hasAnyRollover,
		},
		{
			name: 'actual-billable-value-of-service',
			checked: true,
			translationId: inHours ? 'common.actual_billable_hours' : 'common.actual_billable_value_of_service',
		},
		{
			name: 'actual-budget-hours-remaining',
			checked: true,
			translationId: inHours ? 'retainer.actual_budget_hours_remaining' : 'retainer.actual_budget_remaining',
			condition: hasTarget,
		},
		{
			name: 'actual-percentage-target-used',
			checked: true,
			translationId: inHours ? 'retainer.actual_percentage_target_used' : 'retainer.actual_percentage_budget_used',
			condition: hasTarget,
		},
		{
			name: 'projected-total',
			checked: true,
			translationId: inHours ? 'common.projected_total_hours' : 'common.projected_total_billable_value_of_service',
		},
		{
			name: 'projected-total-budget-remaining',
			checked: true,
			translationId: 'common.projected_total_budget_remaining',
		},
	].filter(option => option.condition ?? true);
	return options;
}
